import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Appointment, AppointmentFormData, AppointmentStatus } from '../models/Appointment';
import { Client } from '../models/Client';

export class AppointmentService {
  private static instance: AppointmentService;
  private appointmentsCollection = collection(db, 'appointments');

  private constructor() {}

  static getInstance(): AppointmentService {
    if (!AppointmentService.instance) {
      AppointmentService.instance = new AppointmentService();
    }
    return AppointmentService.instance;
  }

  async createAppointment(
    companyId: string,
    rbtId: string,
    supervisorId: string,
    client: Client,
    createdBy: string,
    data: AppointmentFormData
  ): Promise<Appointment> {
    const appointmentRef = doc(this.appointmentsCollection);
    const now = new Date();

    // Parse date and times
    const startDateTime = this.parseDateTime(data.startDate, data.startTime);
    const endDateTime = this.parseDateTime(data.startDate, data.endTime);
    const durationMinutes = Math.round((endDateTime.getTime() - startDateTime.getTime()) / 60000);

    const appointment: Appointment = {
      id: appointmentRef.id,
      companyId,
      rbtId,
      supervisorId,
      clientId: data.clientId,
      startTime: startDateTime,
      endTime: endDateTime,
      duration: durationMinutes,
      location: {
        address: client.address,
        city: client.city,
        state: client.state,
        zipCode: client.zipCode,
      },
      supervisionRequired: data.supervisionRequired,
      cameraRequired: data.cameraRequired,
      notes: data.notes,
      isRecurring: data.isRecurring,
      recurrenceRule: data.recurrenceRule,
      status: 'scheduled',
      createdAt: now,
      updatedAt: now,
      createdBy,
    };

    const firestoreData: any = {
      id: appointmentRef.id,
      companyId,
      rbtId,
      supervisorId,
      clientId: data.clientId,
      startTime: Timestamp.fromDate(startDateTime),
      endTime: Timestamp.fromDate(endDateTime),
      duration: durationMinutes,
      location: {
        address: client.address,
        city: client.city,
        state: client.state,
        zipCode: client.zipCode,
      },
      supervisionRequired: data.supervisionRequired,
      cameraRequired: data.cameraRequired,
      isRecurring: data.isRecurring,
      status: 'scheduled',
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
      createdBy,
    };

    // Add optional fields
    if (data.notes) {
      firestoreData.notes = data.notes;
    }
    if (data.recurrenceRule) {
      firestoreData.recurrenceRule = data.recurrenceRule;
    }

    await setDoc(appointmentRef, firestoreData);
    return appointment;
  }

  async getAppointment(appointmentId: string): Promise<Appointment | null> {
    const appointmentRef = doc(this.appointmentsCollection, appointmentId);
    const appointmentSnap = await getDoc(appointmentRef);

    if (!appointmentSnap.exists()) {
      return null;
    }

    return this.mapFirestoreToAppointment(appointmentSnap.id, appointmentSnap.data());
  }

  async getAppointmentsByRbt(rbtId: string): Promise<Appointment[]> {
    const q = query(
      this.appointmentsCollection,
      where('rbtId', '==', rbtId),
      orderBy('startTime', 'asc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      this.mapFirestoreToAppointment(doc.id, doc.data())
    );
  }

  async getAppointmentsBySupervisor(supervisorId: string): Promise<Appointment[]> {
    const q = query(
      this.appointmentsCollection,
      where('supervisorId', '==', supervisorId),
      orderBy('startTime', 'asc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      this.mapFirestoreToAppointment(doc.id, doc.data())
    );
  }

  async getAppointmentsByCompany(companyId: string): Promise<Appointment[]> {
    const q = query(
      this.appointmentsCollection,
      where('companyId', '==', companyId),
      orderBy('startTime', 'asc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      this.mapFirestoreToAppointment(doc.id, doc.data())
    );
  }

  async getUpcomingAppointmentsByRbt(rbtId: string): Promise<Appointment[]> {
    const now = new Date();
    const q = query(
      this.appointmentsCollection,
      where('rbtId', '==', rbtId),
      where('startTime', '>=', Timestamp.fromDate(now)),
      orderBy('startTime', 'asc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      this.mapFirestoreToAppointment(doc.id, doc.data())
    );
  }

  async getUpcomingAppointmentsBySupervisor(supervisorId: string): Promise<Appointment[]> {
    const now = new Date();
    const q = query(
      this.appointmentsCollection,
      where('supervisorId', '==', supervisorId),
      where('startTime', '>=', Timestamp.fromDate(now)),
      orderBy('startTime', 'asc')
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      this.mapFirestoreToAppointment(doc.id, doc.data())
    );
  }

  async updateAppointmentStatus(
    appointmentId: string,
    status: AppointmentStatus
  ): Promise<void> {
    const appointmentRef = doc(this.appointmentsCollection, appointmentId);
    await updateDoc(appointmentRef, {
      status,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  async updateAppointment(
    appointmentId: string,
    updates: Partial<AppointmentFormData>
  ): Promise<void> {
    const appointmentRef = doc(this.appointmentsCollection, appointmentId);
    const updateData: any = {
      updatedAt: Timestamp.fromDate(new Date()),
    };

    // Handle date/time conversion if present
    if (updates.startDate && updates.startTime) {
      const startDateTime = this.parseDateTime(updates.startDate, updates.startTime);
      updateData.startTime = Timestamp.fromDate(startDateTime);
    }
    if (updates.startDate && updates.endTime) {
      const endDateTime = this.parseDateTime(updates.startDate, updates.endTime);
      updateData.endTime = Timestamp.fromDate(endDateTime);
    }
    if (updates.startDate && updates.startTime && updates.endTime) {
      const startDateTime = this.parseDateTime(updates.startDate, updates.startTime);
      const endDateTime = this.parseDateTime(updates.startDate, updates.endTime);
      updateData.duration = Math.round((endDateTime.getTime() - startDateTime.getTime()) / 60000);
    }

    // Copy over other fields
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.supervisionRequired !== undefined) updateData.supervisionRequired = updates.supervisionRequired;
    if (updates.cameraRequired !== undefined) updateData.cameraRequired = updates.cameraRequired;
    if (updates.isRecurring !== undefined) updateData.isRecurring = updates.isRecurring;
    if (updates.recurrenceRule !== undefined) updateData.recurrenceRule = updates.recurrenceRule;

    await updateDoc(appointmentRef, updateData);
  }

  /**
   * Flip past-due 'scheduled' appointments to 'no-show'. Once a session starts,
   * the appointment moves to 'in-progress' (then 'completed'), so an appointment
   * still 'scheduled' after its end time means the RBT never checked in. Returns
   * the list with statuses corrected so callers can render immediately. A late
   * start still recovers it to 'in-progress'. Idempotent.
   */
  async reconcileMissedAppointments(appointments: Appointment[]): Promise<Appointment[]> {
    const now = Date.now();
    return Promise.all(
      appointments.map(async (apt) => {
        if (apt.status === 'scheduled' && apt.endTime.getTime() < now) {
          try {
            await this.updateAppointmentStatus(apt.id, 'no-show');
            return { ...apt, status: 'no-show' as AppointmentStatus };
          } catch (e) {
            console.error('reconcileMissedAppointments failed for', apt.id, e);
          }
        }
        return apt;
      })
    );
  }

  async deleteAppointment(appointmentId: string): Promise<void> {
    const appointmentRef = doc(this.appointmentsCollection, appointmentId);
    await deleteDoc(appointmentRef);
  }

  async cancelAppointment(appointmentId: string): Promise<void> {
    await this.updateAppointmentStatus(appointmentId, 'cancelled');
  }

  private parseDateTime(dateString: string, timeString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    const [hours, minutes] = timeString.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  }

  private mapFirestoreToAppointment(id: string, data: any): Appointment {
    return {
      id,
      companyId: data.companyId,
      rbtId: data.rbtId,
      supervisorId: data.supervisorId,
      clientId: data.clientId,
      startTime: data.startTime?.toDate() || new Date(),
      endTime: data.endTime?.toDate() || new Date(),
      duration: data.duration,
      location: data.location,
      supervisionRequired: data.supervisionRequired,
      cameraRequired: data.cameraRequired,
      notes: data.notes,
      isRecurring: data.isRecurring,
      recurrenceRule: data.recurrenceRule,
      parentAppointmentId: data.parentAppointmentId,
      status: data.status,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      createdBy: data.createdBy,
    };
  }
}

// Singleton instance
export const appointmentService = AppointmentService.getInstance();
